/* exported fetchWithDeadline */
/**
 * Bound transport AND body decoding; preserve caller cancellation and HTTP policy.
 * No retries: a timed-out cart write may already have been applied by Shopify.
 * @template T
 * @param {string} url Locale-aware URL supplied by the existing route owner.
 * @param {RequestInit} options
 * @param {(response: Response) => Promise<T>} readResponse
 * @param {number} [timeoutMs]
 * @returns {Promise<T>}
 */
async function fetchWithDeadline(
  url,
  options,
  readResponse,
  timeoutMs = 10000,
) {
  const upstream = options?.signal;
  upstream?.throwIfAborted();
  const controller = new AbortController();
  const cancel = () => controller.abort(upstream.reason);
  let onAbort;
  const cancelled = new Promise((_, reject) => {
    onAbort = () => reject(controller.signal.reason);
    controller.signal.addEventListener("abort", onAbort, { once: true });
  });
  upstream?.addEventListener("abort", cancel, { once: true });
  const timer = setTimeout(() => {
    controller.abort(
      new DOMException("Request deadline exceeded", "TimeoutError"),
    );
  }, timeoutMs);
  try {
    return await Promise.race([
      (async () => {
        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });
        controller.signal.throwIfAborted();
        const data = await readResponse(response);
        controller.signal.throwIfAborted();
        return data;
      })(),
      cancelled,
    ]);
  } finally {
    clearTimeout(timer);
    upstream?.removeEventListener("abort", cancel);
    controller.signal.removeEventListener("abort", onAbort);
  }
}
